<template>
  <v-dialog
    v-model="dialog"
    max-width="800"
    persistent
    scrollable
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="headline primary white--text">
        <font-awesome-icon
          :icon="['fas', 'tools']"
          class="mr-2"
        />
        Review Configuration Changes
      </v-card-title>

      <v-divider />

      <!-- Content -->
      <v-card-text class="pt-4">
        <v-alert
          type="info"
          outlined
          class="mb-4"
        >
          <div class="font-weight-medium mb-2">
            The following documents will be created for <strong>{{ client.clientName || client.billingCode }}</strong>:
          </div>
          <ul class="ml-4">
            <li v-if="!hasConfig">
              Organization Configuration
            </li>
            <li v-if="willCreateEnterprise">
              Enterprise Document
            </li>
            <li v-else-if="enterpriseExists">
              Enterprise Document (already exists - no changes)
            </li>
            <li v-if="willCreateDomain">
              Domain Document
            </li>
            <li v-else-if="domainExists">
              Domain Document (already exists - no changes)
            </li>
          </ul>
        </v-alert>

        <!-- Organization Config Preview -->
        <div
          v-if="!hasConfig"
          class="mb-4"
        >
          <v-card outlined>
            <v-card-title :class="previewSectionTitleClass">
              <font-awesome-icon
                :icon="['fas', 'building']"
                class="mr-2"
                size="sm"
              />
              Organization Configuration
              <v-chip
                x-small
                color="success"
                class="ml-2"
              >
                New
              </v-chip>
            </v-card-title>

            <v-card-text class="py-2">
              <div class="preview-field">
                <div class="field-label">
                  Billing Code
                </div>
                <div class="field-value">
                  {{ previewOrganization.enterpriseId }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Active Status
                </div>
                <div class="field-value">
                  <v-chip
                    x-small
                    :color="previewOrganization.isActive ? 'success' : 'error'"
                  >
                    {{ previewOrganization.isActive ? 'Active' : 'Inactive' }}
                  </v-chip>
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Access Active
                </div>
                <div class="field-value">
                  <v-chip
                    x-small
                    :color="previewOrganization.access.active ? 'success' : 'error'"
                  >
                    {{ previewOrganization.access.active ? 'Yes' : 'No' }}
                  </v-chip>
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Access Expires
                </div>
                <div class="field-value">
                  {{ previewOrganization.access.expires || 'Never' }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Pacing Permission
                </div>
                <div class="field-value">
                  <font-awesome-icon
                    :icon="['fas', previewOrganization.access.permissions.pacing ? 'check' : 'times']"
                    :class="previewOrganization.access.permissions.pacing ? 'success--text' : 'error--text'"
                  />
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Discrepancy Permission
                </div>
                <div class="field-value">
                  <font-awesome-icon
                    :icon="['fas', previewOrganization.access.permissions.discrepancy ? 'check' : 'times']"
                    :class="previewOrganization.access.permissions.discrepancy ? 'success--text' : 'error--text'"
                  />
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Exports Permission
                </div>
                <div class="field-value">
                  <font-awesome-icon
                    :icon="['fas', previewOrganization.access.permissions.exports ? 'check' : 'times']"
                    :class="previewOrganization.access.permissions.exports ? 'success--text' : 'error--text'"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Enterprise Preview -->
        <div
          v-if="willCreateEnterprise || enterpriseExists"
          class="mb-4"
        >
          <v-card outlined>
            <v-card-title :class="previewSectionTitleClass">
              <font-awesome-icon
                :icon="['fas', 'briefcase']"
                class="mr-2"
                size="sm"
              />
              Enterprise Document
              <v-chip
                v-if="willCreateEnterprise"
                x-small
                color="success"
                class="ml-2"
              >
                New
              </v-chip>
              <v-chip
                v-else
                x-small
                color="info"
                class="ml-2"
              >
                Exists
              </v-chip>
            </v-card-title>

            <v-card-text class="py-2">
              <div class="preview-field">
                <div class="field-label">
                  Enterprise ID
                </div>
                <div class="field-value">
                  {{ displayEnterprise.enterpriseId }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Enterprise Name
                </div>
                <div class="field-value">
                  {{ displayEnterprise.enterpriseName }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Domains
                </div>
                <div class="field-value">
                  <v-combobox
                    v-if="willCreateEnterprise"
                    v-model="editableEnterpriseDomains"
                    :items="[]"
                    chips
                    clearable
                    dense
                    multiple
                    outlined
                    hide-details="auto"
                    placeholder="Type domain and press Enter"
                    hint="Email domains authorized for this enterprise"
                    small-chips
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        close
                        small
                        @click:close="removeEnterpriseDomain(index)"
                      >
                        {{ item }}
                      </v-chip>
                    </template>
                  </v-combobox>
                  <span v-else>
                    {{ displayEnterprise.domains?.join(', ') || 'None' }}
                  </span>
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Organizations
                </div>
                <div class="field-value">
                  {{ displayEnterprise.organizations?.join(', ') || 'None' }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Active Status
                </div>
                <div class="field-value">
                  <v-chip
                    x-small
                    :color="displayEnterprise.isActive ? 'success' : 'error'"
                  >
                    {{ displayEnterprise.isActive ? 'Active' : 'Inactive' }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Domain Preview -->
        <div
          v-if="willCreateDomain || (domainExists && isTenant)"
          class="mb-4"
        >
          <v-card outlined>
            <v-card-title :class="previewSectionTitleClass">
              <font-awesome-icon
                :icon="['fas', 'globe']"
                class="mr-2"
                size="sm"
              />
              Domain Document
              <v-chip
                v-if="willCreateDomain"
                x-small
                color="success"
                class="ml-2"
              >
                New
              </v-chip>
              <v-chip
                v-else
                x-small
                color="info"
                class="ml-2"
              >
                Exists
              </v-chip>
            </v-card-title>

            <v-card-text class="py-2">
              <div class="preview-field">
                <div class="field-label">
                  Domain Name
                </div>
                <div class="field-value">
                  {{ displayDomain.domainName }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Short Name
                </div>
                <div class="field-value">
                  {{ displayDomain.shortName }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Enterprises
                </div>
                <div class="field-value">
                  {{ displayDomain.enterprises?.join(', ') || 'None' }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Primary Provider
                </div>
                <div class="field-value">
                  {{ displayDomain.providers?.[0]?.providerId || 'N/A' }}
                </div>
              </div>

              <div class="preview-field">
                <div class="field-label">
                  Active Status
                </div>
                <div class="field-value">
                  <v-chip
                    x-small
                    :color="displayDomain.isActive ? 'success' : 'error'"
                  >
                    {{ displayDomain.isActive ? 'Active' : 'Inactive' }}
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <v-alert
          v-if="error"
          type="error"
          outlined
          class="mt-4"
        >
          {{ error }}
        </v-alert>
      </v-card-text>

      <v-divider />

      <!-- Actions -->
      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          :disabled="loading"
          @click="cancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="loading"
          @click="confirm"
        >
          <font-awesome-icon
            :icon="['fas', 'check']"
            class="mr-2"
          />
          Create Documents
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import { createNamespacedHelpers } from 'vuex'
import { last } from 'underscore'

import { firecollection } from '@/store/firebase.js'
import themeClasses from '@/mixins/themeClasses'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

const isDefinedObject = (val) => val !== null && typeof val === 'object'
const hasObjectKeys = (val) => Object.keys(val).length > 0

export default {
  name: 'FixConfigDialog',
  mixins: [themeClasses],
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default: null,
    },
    publisher: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      enterpriseExists: false,
      domainExists: false,
      existingEnterprise: null,
      existingDomain: null,
      editableEnterpriseDomains: [],
    }
  },
  computed: {
    ...mapAuthGetters([
      'self',
      'isInternalUser',
    ]),
    previewSectionTitleClass() {
      return `subtitle-1 font-weight-bold py-2 ${this.themeSurfaceColor('grey lighten-4', 'darkblue lighten-4')}`
    },

    currentUser() {
      return this.self
    },

    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    hasConfig() {
      return isDefinedObject(this.config) && hasObjectKeys(this.config)
    },

    userEmail() {
      return this.currentUser.email || 'unknown'
    },

    userPersonId() {
      return this.currentUser.personId || 'unknown'
    },

    userUid() {
      return this.currentUser.uid || 'unknown'
    },

    userDomain() {
      if (this.isInternalUser) {
        return null
      }

      return this.userEmail.split('@')[1]
    },

    isTenant() {
      if (this.isInternalUser) {
        return false
      }

      return this.config.isTenant || false
    },

    willCreateEnterprise() {
      return !this.enterpriseExists
    },

    willCreateDomain() {
      return this.isTenant && !this.domainExists && this.userDomain
    },

    previewOrganization() {
      const { billingCode, isActive } = this.client

      return {
        access: {
          active: isActive,
          expires: null,
          permissions: {
            adxHealth: false,
            customReports: [],
            discrepancy: true,
            exports: true,
            networkHealth: false,
            pacing: true,
            teams: false,
            yieldPartner: false,
          },
        },
        committerUid: this.userPersonId,
        committerEmail: this.userEmail,
        createdByEmail: this.userEmail,
        createdByUid: this.userPersonId,
        createdOn: new Date().toISOString(),
        enterpriseId: billingCode,
        isActive,
        lastModifiedByEmail: this.userEmail,
        lastModifiedByUid: this.userPersonId,
        lastModifiedOn: new Date().toISOString(),
        settings: {
          filters: [],
          teams: [],
        },
      }
    },

    previewEnterprise() {
      const { userEmail, userPersonId, userUid } = this
      const { billingCode, isActive } = this.client

      return {
        billingCode,
        createdByEmail: userEmail,
        createdByUid: userPersonId,
        createdOn: new Date().toISOString(),
        enterpriseId: billingCode,
        enterpriseName: this.client.clientName || this.publisher?.publisherName || billingCode,
        lastModified: new Date().toISOString(),
        lastModifiedByEmail: userEmail,
        lastModifiedByUid: userPersonId,
        committerUid: userUid,
        committerEmail: userEmail,
        docId: billingCode,
        domains: this.editableEnterpriseDomains,
        isActive,
        isTenant: this.isTenant,
        organizations: [billingCode],
      }
    },

    previewDomain() {
      const { userDomain, userEmail, userPersonId, userUid } = this
      const { billingCode, isActive } = this.client

      return {
        domainName: this.userDomain,
        createdOn: new Date().toISOString(),
        createdByEmail: userEmail,
        createdByUid: userUid,
        committerUid: userUid,
        committerEmail: userEmail,
        docId: userDomain,
        enterprises: [billingCode],
        isActive,
        isPrimary: true,
        isTenant: this.isTenant,
        lastModifiedOn: new Date().toISOString(),
        lastModifiedByUid: userPersonId,
        lastModifiedByEmail: userEmail,
        providerIds: ['google.com'],
        providers: [
          {
            isActive: true,
            isPrimary: true,
            providerId: 'google.com',
            createdOn: new Date().toISOString(),
            createdByUid: userUid,
            createdByEmail: userEmail,
            lastModifiedOn: new Date().toISOString(),
            lastModifiedByUid: userPersonId,
            lastModifiedByEmail: userEmail,
          },
        ],
        shortName: last(userDomain.split('.')) || userDomain,
        tenantHash: null,
      }
    },

    displayEnterprise() {
      return this.enterpriseExists && this.existingEnterprise
        ? this.existingEnterprise
        : this.previewEnterprise
    },

    displayDomain() {
      return this.domainExists && this.existingDomain
        ? this.existingDomain
        : this.previewDomain
    },
  },
  async mounted() {
    await this.checkExistingDocuments()
    // Initialize editable domains with user's domain if available
    if (this.userDomain && !this.enterpriseExists) {
      this.editableEnterpriseDomains = [this.userDomain]
    }
  },
  methods: {
    async checkExistingDocuments() {
      try {
        // Check if enterprise exists and fetch its data
        const enterpriseDoc = await firecollection.get('enterprise', this.client.billingCode)
        this.enterpriseExists = enterpriseDoc?.exists || false
        if (this.enterpriseExists) {
          this.existingEnterprise = enterpriseDoc.data()
        }

        // Check if domain exists and fetch its data
        if (this.isTenant && this.userDomain) {
          const domainDoc = await firecollection.get('domain', this.userDomain)
          this.domainExists = domainDoc?.exists || false
          if (this.domainExists) {
            this.existingDomain = domainDoc.data()
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking existing documents:', error)
        this.enterpriseExists = false
        this.domainExists = false
        this.existingEnterprise = null
        this.existingDomain = null
      }
    },

    removeEnterpriseDomain(index) {
      this.editableEnterpriseDomains.splice(index, 1)
    },

    cancel() {
      this.dialog = false
      this.error = null
    },

    async confirm() {
      this.loading = true
      this.error = null

      const { billingCode } = this.client

      if (!billingCode) {
        this.error = 'Cannot fix config: Missing billing code'
        this.loading = false
        return
      }

      try {
        const createdDocs = []

        // Create organization config
        if (!this.hasConfig) {
          await firecollection.set('organization', billingCode, this.previewOrganization, { merge: true })
          createdDocs.push('Organization')
        }

        // Create enterprise if needed
        if (this.willCreateEnterprise) {
          await firecollection.set('enterprise', billingCode, this.previewEnterprise, { merge: true })
          createdDocs.push('Enterprise')
        }

        // Create domain if needed
        if (this.willCreateDomain) {
          await firecollection.set('domain', this.userDomain, this.previewDomain, { merge: true })
          createdDocs.push('Domain')
        }

        // Success notification
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Created: ${createdDocs.join(', ')} for ${this.client.clientName || billingCode}`,
          status: 'success',
        }, { root: true })

        // Emit success event
        this.$emit('success', billingCode)

        // Close dialog
        this.dialog = false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating documents:', error)

        this.error = `Failed to create documents: ${error.message || 'Unknown error'}`

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: this.error,
          status: 'error',
        }, { root: true })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.preview-field {
  display: flex;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }

  .field-label {
    font-weight: 500;
    min-width: 200px;
    flex-shrink: 0;
  }

  .field-value {
    flex-grow: 1;
  }
}

.theme--dark {
  .preview-field {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }
}
</style>
